/// <amd-module name="Agile/Sprints/Directory/Redux/SprintDirectorySaga" />
import { getSprintsContentRoute } from "Agile/Common/Routes/SprintsRoutingHelpers";
import { ExceptionInfo } from "Agile/Contracts/Common";
import { DirectoryFilterKeys } from "Agile/Directory/DirectoryConstants";
import { IDirectoryContext } from "Agile/Directory/DirectoryContracts";
import { DirectoryActions } from "Agile/Directory/Redux/DirectoryActions";
import { getArtifactById, getArtifactMap } from "Agile/Directory/Redux/DirectorySelectors";
import { ISprintsDirectoryDataProviderData } from "Agile/Sprints/Directory/SprintDirectoryContracts";
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { ISprintDirectoryArtifact } from "SprintDirectoryContracts";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageData";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { HubViewStateActions, HubViewStateActionTypes } from "VSS/Redux/Deprecated/HubViewState";
import { NavigationActions } from "VSS/Redux/Modules/Navigation";
import { SprintDirectoryActions, SprintDirectoryActionTypes } from "./SprintDirectoryActions";

export function* sprintDirectorySaga(): SagaIterator {
    yield effects.takeEvery(HubViewStateActionTypes.FilterStateChanged, sprintFilterChanged);
    yield effects.takeEvery(SprintDirectoryActionTypes.EditSprintSucceeded, editSprintSucceeded);
    yield effects.takeEvery(SprintDirectoryActionTypes.NewSprintSucceeded, newSprintSucceeded);
}

export function* sprintFilterChanged(action: ActionsOfType<HubViewStateActions, HubViewStateActionTypes.FilterStateChanged>): SagaIterator {
    const filterStateChange = action.payload;
    if (filterStateChange[DirectoryFilterKeys.TEAM_FILTER_KEY] != null) {
        // team filter was changed, page more teams
        const teamFilterState = filterStateChange[DirectoryFilterKeys.TEAM_FILTER_KEY];
        const filterTeamIDs = teamFilterState ? teamFilterState.value.map(f => f.key) : [];
        const pagedArtifacts: {
            [key: string]: ISprintDirectoryArtifact;
        } = (yield effects.select(getArtifactMap)) as any;
        const pagedTeamIds = Object.keys(pagedArtifacts);

        if (filterTeamIDs.length > 0) {
            // Check if there are any unpaged artifacts and fetch them asynchronously
            const teamsToLoad: string[] = filterTeamIDs.filter(i => pagedTeamIds.indexOf(i) < 0);
            if (teamsToLoad && teamsToLoad.length > 0) {
                yield effects.call(loadUnpagedTeams, teamsToLoad);
            }
        }
    }
}

export function* loadUnpagedTeams(teamIds: string[]): SagaIterator {
    // Pop the load all data spinner
    yield effects.put(DirectoryActions.beginLoadAllData());

    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;

    const dataProviderData: ISprintsDirectoryDataProviderData = (yield effects.call(
        [contributionService, contributionService.getDataAsync],
        directoryContext.allDataProviderId,
        { TeamIds: teamIds }
    )) as any;
    const validationResult = _verifyAndReportValidData(dataProviderData);
    if (validationResult) {
        yield effects.put(DirectoryActions.loadAllDataFailed(validationResult.exceptionInfo));
    } else {
        yield effects.put(DirectoryActions.loadAllDataSucceeded(dataProviderData));
    }
}

export function* editSprintSucceeded(action: ActionsOfType<SprintDirectoryActions, SprintDirectoryActionTypes.EditSprintSucceeded>): SagaIterator {
    // Sprint update succeeded, update the artifact in the directory state

    const { team, iteration } = action.payload;

    // Get the current artifact
    const sprintArtifact: ISprintDirectoryArtifact = (yield effects.select(getArtifactById(team.id))) as any;

    // Copy it
    const updatedArtifact: ISprintDirectoryArtifact = { ...sprintArtifact };

    // Update the fields
    updatedArtifact.iterationName = iteration.name;

    // NOTE: Because we are working with a legacy component, they still use shifted UTC dates.
    // We are moving away from that in the new areas, so adjust back
    updatedArtifact.iterationStartDate = iteration.startDateUTC ? shiftToLocal(iteration.startDateUTC) : undefined;
    updatedArtifact.iterationEndDate = iteration.finishDateUTC ? shiftToLocal(iteration.finishDateUTC) : undefined;

    // Dispatch the updated artifact
    yield effects.put(DirectoryActions.artifactUpdated(team.id, updatedArtifact));
}

export function* newSprintSucceeded(action: ActionsOfType<SprintDirectoryActions, SprintDirectoryActionTypes.NewSprintSucceeded>): SagaIterator {
    // Sprint created, navigate to it

    const { team, iteration } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const project: IProjectData = (yield effects.select(currentProject)) as any;

    const contentUrl: string = (yield effects.call(getSprintsContentRoute, pageContext, project.name, team.name, iteration.iterationPath)) as any;
    yield effects.put(NavigationActions.fpsNavigateToUrl(contentUrl));
}

function _verifyAndReportValidData(pageData: ISprintsDirectoryDataProviderData): { message: string; exceptionInfo: ExceptionInfo } | undefined {
    if (!pageData || !pageData.artifacts) {
        return {
            message: "The data provider didn't return any data",
            exceptionInfo: { exceptionMessage: "Server returned no response" }
        };
    } else if (pageData.exceptionInfo) {
        return {
            message: `The data provider contained exception info: ${JSON.stringify(pageData.exceptionInfo)}`,
            exceptionInfo: pageData.exceptionInfo
        };
    }
}

function shiftToLocal(date: Date): Date {
    const minute = 1000 * 60;
    return new Date(date.getTime() - date.getTimezoneOffset() * minute);
}
